import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack, IconButton, InputAdornment, List, ListItem, ListItemText, ListItemSecondaryAction, Divider, Switch, CircularProgress, Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import {
  getScheduledFlows, createScheduledFlow, updateScheduledFlow, deleteScheduledFlow,
} from '../../api/automation.api.js';

function ScheduledFlows() {
  const [flows, setFlows] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editFlow, setEditFlow] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', schedule: '', enabled: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFlows = async () => {
    setLoading(true); setError(null);
    try {
      const data = await getScheduledFlows();
      setFlows(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFlows(); }, []);

  const filteredFlows = flows.filter(
    (f) => f.name.toLowerCase().includes(search.toLowerCase())
      || f.description.toLowerCase().includes(search.toLowerCase()),
  );

  const handleOpenDialog = (flow = null) => {
    setEditFlow(flow);
    setForm(flow ? {
      name: flow.name, description: flow.description, schedule: flow.schedule, enabled: flow.enabled,
    } : {
      name: '', description: '', schedule: '', enabled: true,
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditFlow(null);
    setForm({
      name: '', description: '', schedule: '', enabled: true,
    });
  };

  const handleSave = async () => {
    setLoading(true); setError(null);
    try {
      if (editFlow) {
        const updated = await updateScheduledFlow(editFlow._id || editFlow.id, form);
        setFlows((prev) => prev.map((f) => (f._id === updated._id ? updated : f)));
      } else {
        const created = await createScheduledFlow(form);
        setFlows((prev) => [created, ...prev]);
      }
      handleCloseDialog();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setLoading(true); setError(null);
    try {
      await deleteScheduledFlow(id);
      setFlows((prev) => prev.filter((f) => f._id !== id && f.id !== id));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Scheduled Flows</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          New Scheduled Flow
        </Button>
      </Stack>
      <TextField
        placeholder="Search scheduled flows..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />
      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <List>
        {filteredFlows.map((flow) => (
          <React.Fragment key={flow._id || flow.id}>
            <ListItem alignItems="flex-start">
              <ListItemText
                primary={flow.name}
                secondary={(
                  <>
                    {flow.description}
                    <br />
                    <span style={{ color: '#888' }}>
                      Schedule:
                      {flow.schedule}
                      {' '}
                      | Next:
                      {flow.nextRun ? new Date(flow.nextRun).toLocaleString() : 'N/A'}
                    </span>
                  </>
                )}
              />
              <Switch
                checked={flow.enabled}
                onChange={async () => {
                  setLoading(true); setError(null);
                  try {
                    const updated = await updateScheduledFlow(flow._id || flow.id, { ...flow, enabled: !flow.enabled });
                    setFlows((prev) => prev.map((f) => (f._id === updated._id ? updated : f)));
                  } catch (err) {
                    setError(err.message);
                  } finally {
                    setLoading(false);
                  }
                }}
                color="primary"
                inputProps={{ 'aria-label': 'Enable/disable scheduled flow' }}
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => handleOpenDialog(flow)}>
                  <EditIcon />
                </IconButton>
                <IconButton edge="end" color="error" onClick={() => handleDelete(flow._id || flow.id)}>
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
            <Divider component="li" />
          </React.Fragment>
        ))}
        {filteredFlows.length === 0 && !loading && (
          <ListItem>
            <ListItemText primary="No scheduled flows found." />
          </ListItem>
        )}
      </List>
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editFlow ? 'Edit Scheduled Flow' : 'New Scheduled Flow'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
          <TextField
            label="Schedule (cron)"
            value={form.schedule}
            onChange={(e) => setForm((f) => ({ ...f, schedule: e.target.value }))}
            fullWidth
            margin="normal"
            placeholder="e.g. 0 8 * * * for every day at 8am"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {editFlow ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ScheduledFlows;
